// poiesis/src/views/catalog.ts
// Reads the served view-kind catalog (GET /view-kinds) and runs the gating rule
// client-side for the picker/offer UI. Same catalog the backend/LLM/DSL consume.
const BASE = (import.meta as any).env?.VITE_MONAD_BASE ?? ''

export interface RoleSpec {
  name: string; accepts: string[]; required: boolean
  arity?: 'to-one' | 'repeatable'; interval?: boolean; reduce?: boolean
  /** SEN-706: the substrate itself satisfies this role (telemetry time axis,
   *  Aion quantities) — offerable without any matching object attribute.
   *  Flows from the backend RoleSpec dataclass via asdict → GET /view-kinds. */
  substrate_satisfiable?: boolean
  /** CEO: the binding must be a non-reverse entity-reference whose
   *  target_type_id == the current type — prevents offering tree when the
   *  only entity-refs point elsewhere (every record would be a root).
   *  Flows from the backend RoleSpec dataclass via asdict → GET /view-kinds. */
  target_self?: boolean
  /** Kinds accepted only when the user drills into a composite sub-field. */
  accepts_subfields?: string[] | null
  /** Default binding ranks accepted composite leaves ahead of whole attrs. */
  prefers_subfields?: boolean
  /** Default binding prefers the subject type's kenning path. */
  prefers_identity?: boolean
  /** False leaves an optional role unbound by default while keeping it pickable. */
  autobind?: boolean
  /** 2026-10-22: where the role is offered and honoured (CONFIG_SURFACES);
   *  absent = every surface. The table's `group` is 'view' only. */
  surfaces?: ConfigSurface[]
  /** Binding this role decorates each identity row with its own chip. Served
   *  metadata keeps the generic frame pipeline free of role-name branches. */
  decorates_identity?: boolean
  /** SEN-730 (M3): composite attribute types that produce multiple dim_cell columns
   *  — one per sub-field. When present, buildReduceRequest expands the bound slug to
   *  "<boundSlug>.<sub>" for each declared sub-field instead of the bare slug.
   *  The dot-join convention "<slug>.<sub>" is owned by
   *  monad/aion/dim_explode.py::explode_dim_rows (f"{attribute}.{sub.name}")
   *  — this frontend expansion MUST match exactly (drift guard, audit Q2a).
   *  Flows from the backend RoleSpec dataclass via asdict → GET /view-kinds. */
  consumes_subfields?: string[]
  /** 2026-09-02: the role is a CYCLIC time axis satisfied by the substrate
   *  (heatmap x/y). Value = default part; option "<role>_part" overrides.
   *  buildReduceRequest emits `time-part(<part>)` instead of a slug. */
  time_part?: string | null
}
/** SEN-1411 (T6/F3): per-density render policy — mirrors the backend
 *  DensityPolicy dataclass (monad/view_kinds_registry.py). Flows over
 *  GET /view-kinds like every other spec field. */
export interface DensityPolicy {
  chrome: string[]                    // which chrome renders (toolbar|legend|axes|annotations)
  max_rows: number | null             // data budget; null = uncapped
  overflow: 'scroll' | 'note'        // affordance when the cap bites
  interactions: boolean               // interaction budget
  variant: string | null              // alternate renderer key (e.g. 'column-counts')
  /** SEN-433 (A4): the renderer draws to its card's edges — the host's card
   *  body has no padding. Optional on the wire. */
  edge_to_edge?: boolean
}
export type Density = 'full' | 'peek' | 'block'

/** SEN-1431 (ADR-0036): one declared, vocabulary-bound option of a view kind —
 *  mirrors the backend OptionField dataclass (monad/view_kinds_registry.py).
 *  Registration is fail-closed server-side; ONE SettingsForm walks the schema
 *  client-side (src/views/SettingsForm.tsx — closed switch, conformance-guarded).
 *  Flows over GET /view-kinds like every other spec field. */
export type OptionKind = 'toggle' | 'select' | 'segmented' | 'number' | 'text' | 'secret' | 'oauth'
/** Mirror of monad/view_kinds_registry.py OPTION_KINDS — extend BOTH. */
export const OPTION_KINDS: readonly OptionKind[] = ['toggle', 'select', 'segmented', 'number', 'text', 'secret', 'oauth']

/** Mirror of monad/view_kinds_registry.py ConfigSurface / CONFIG_SURFACES —
 *  extend BOTH. `view` = the Records-canvas strip; `widget` = a dashboard tile;
 *  `record` = a widget on a record page's layout (the widget's options; its own period allowed). */
export type ConfigSurface = 'view' | 'widget' | 'record'
export const CONFIG_SURFACES: readonly ConfigSurface[] = ['view', 'widget', 'record']

export interface OptionField {
  key: string                          // config key the renderer reads (F-A threads it)
  kind: OptionKind                     // CLOSED vocabulary
  label: string
  default: unknown
  choices: string[] | null             // select/segmented
  min: number | null                   // number
  max: number | null                   // number
  visible_when: Record<string, unknown> | null  // {declared option key: value}
  /** Choices projected from metadata at edit time: 'categorical_attributes' =
   *  the static `choices` plus the subject type's categorical attribute slugs.
   *  The catalog never names a tenant attribute. */
  projected_from?: string | null
  /** A select whose value is a LIST of choices (mirrors OptionField.multiple). */
  multiple?: boolean
  /** SEN-1767: WHERE this option is offered — mirrors the backend
   *  OptionField.surfaces (monad/view_kinds_registry.py). Optional on the WIRE
   *  type so an older backend can't crash the SPA; absent = both surfaces,
   *  which is also the server's default. */
  surfaces?: ConfigSurface[]
  /** SEN-1756: display names for wire slugs — served on STRUCTURAL rows only
   *  today (declared options keep raw choices); the choice primitives draw
   *  the label, the value stays the slug. Optional everywhere. */
  choice_labels?: Record<string, string> | null
  /** A text option that is a template over the cells of the record bound to
   *  this role ({kenning}, {<slug>}) — mirrors OptionField.template_of; the
   *  host reads the named cells with one records/values. */
  template_of?: string | null
  /** A7: a text option's value must fullmatch this regex (server-validated). */
  pattern?: string | null
  /** The binding form (Widget.bind key, e.g. 'records') this option needs —
   *  mirrors OptionField.requires_bind; offered only on a widget carrying it. */
  requires_bind?: string | null
}

/** SEN-1756: one row of a kind's served STRUCTURAL editable shape
 *  (GET /view-kinds → structural_schema) — OptionField's wire shape plus
 *  composition metadata. The rows are derived server-side from the registry's
 *  own gates (monad/view_kinds_registry.py::structural_fields); the client
 *  renders them generically and NEVER knows a key's name.
 *  - `group`: rows sharing a group compose ONE structural value; the row whose
 *    key === group is the PRIMARY. Primary at its default = "off" → the group
 *    writes nothing (absent key IS the substrate default).
 *  - `config_path`: where the value lives in the widget config (nested). */
export interface StructuralField extends OptionField {
  group: string
  config_path: string[]
  /** Owner 2026-10-29 (the bucket row only): the grains a READER switches a
   *  time chart between and the default by the window's length ([longest
   *  days | null, grain], first that holds) — the server's grain registry
   *  (views/bucket.ts reads them). Owner 2026-10-30: no grain has a longest
   *  window; `points_per_request` is the most buckets ONE chart question
   *  reads — a longer window is read in chunks (views/chunks.ts). */
  viewer_choices?: string[] | null
  default_by_span?: Array<[number | null, string]> | null
  points_per_request?: number | null
}

/** Mirror of monad/view_kinds_registry.py LayoutKind — the CLOSED renderer-key
 *  vocabulary; every member has a registered renderer (layoutKinds.conformance
 *  test). Extend BOTH. */
export const LAYOUT_KINDS = ['cartesian', 'geo', 'hierarchy', 'interval', 'board', 'pivot', 'calendar', 'stat', 'matrix',
  'table', 'item_card'] as const
export type LayoutKind = typeof LAYOUT_KINDS[number]

export interface ViewKindSpec {
  kind: string; layout_kind: string; default_reducer: string
  roles: RoleSpec[]
  /** At least one named role must be satisfiable (e.g. map position|outline). */
  requires_any_roles?: string[]
  /** SEN-1756. Optional on the WIRE type so an older backend can't crash the
   *  SPA; absent → no structural controls are offered. */
  structural_schema?: StructuralField[]
  /** Optional on the WIRE type so an older backend can't crash the SPA. */
  config_schema?: OptionField[]
  /** Optional on the WIRE type so an older backend can't crash the SPA;
   *  ViewHost falls back to a permissive full policy when absent. */
  densities?: Record<string, DensityPolicy>
  /** SEN-1444: which FrameMark kinds this kind's renderers actually draw —
   *  mirrors ViewKindSpec.marks_supported (monad/view_kinds_registry.py),
   *  fail-closed at registration server-side. ViewHost gates marks against it
   *  (gateMarks in engine.tsx — drops WITH a dev-console warning, defense in
   *  depth behind monad/poiesis/marks.py). Optional on the WIRE type so an
   *  older backend can't crash the SPA; absent → permissive. */
  marks_supported?: string[]
  /** Task 10 fix round 2 (R10): the dashboard grid's per-kind height floor
   *  (cells) — mirrors ViewKindSpec.min_h (monad/view_kinds_registry.py).
   *  Optional on the WIRE type so an older backend can't crash the SPA;
   *  DashboardPage.tsx's minHeightFor falls back to the shell's universal
   *  floor (viewTheme.dashboard.minH) when absent. */
  min_h?: number
  /** SEN-1769: per-axis scale origin — mirrors ViewKindSpec.axis_baseline
   *  (monad/view_kinds_registry.py), keys 'x' | 'y'. Optional on the WIRE type
   *  so an older backend can't crash the SPA; an absent axis keeps the
   *  historical zero origin. */
  axis_baseline?: Record<string, AxisBaseline>
  /** SEN-1756: the kind honours a ranked cut (rank/granularity structural
   *  keys) — mirrors ViewKindSpec.supports_rank. A ranked cut ranks a
   *  MEASURE, so the widget editor offers the measure select on this row
   *  even without a reduce role. Optional on the WIRE type. */
  supports_rank?: boolean
  /** The kind honours a fixed row order (`sort`) — mirrors ViewKindSpec.supports_sort. */
  supports_sort?: boolean
  /** 2026-10-23: a scalar kind that, given `config.granularity`, reads a
   *  time-bucket frame and draws its LATEST bucket with its time (the
   *  "current" reading) — mirrors ViewKindSpec.over_time. Optional on the wire. */
  over_time?: boolean
  /** SEN-433: the renderer draws its own pending state (and a refused
   *  question) — ViewHost mounts it with `pending` instead of "Loading…".
   *  Mirrors ViewKindSpec.renders_pending. Optional on the wire. */
  renders_pending?: boolean
  /** SEN-433: the renderer IS its card — a host draws no tile border or card
   *  header around it. Mirrors ViewKindSpec.self_framed. Optional on the wire. */
  self_framed?: boolean
}

/** Mirror of monad/view_kinds_registry.py AXIS_BASELINES — extend BOTH. */
export type AxisBaseline = 'zero' | 'data'

export async function getViewKinds(): Promise<ViewKindSpec[]> {
  const resp = await fetch(`${BASE}/view-kinds`)
  if (!resp.ok) throw new Error(`GET /view-kinds failed: ${resp.status}`)
  return resp.json()
}

export interface OfferableAttr {
  kind: string
  is_multiselect: boolean
  /** target_type_id from entity-reference attributes — required for target_self
   *  role evaluation. Present at runtime via .passthrough() on the API schema. */
  target_type_id?: string | null
  subfield_kinds?: string[]
}

/** Mirror of the backend offerable() rule (monad/view_kinds_registry.py — change
 *  both). SEN-714: reduce roles bind ONLY quantities (the binder renders a
 *  quantity selector, no path picker); substrate_satisfiable roles are satisfied
 *  by carried telemetry OR a matching attribute. target_self roles require a
 *  non-reverse entity-reference attr whose target_type_id == currentTypeId.
 *  Callers must pass FORWARD attributes only (reverse refs never reach dim_cell
 *  — they can't chart). */
export function offerable(
  spec: ViewKindSpec,
  attributes: OfferableAttr[],
  hasQuantities: boolean,
  currentTypeId?: string,
  /** the surface asking — a kind whose required role is not offered there is not offerable there */
  surface?: ConfigSurface,
): boolean {
  if (surface && spec.roles.some((r) => r.required && r.surfaces && !r.surfaces.includes(surface))) return false
  const roleIsSatisfiable = (role: RoleSpec): boolean => {
    if (role.reduce) return hasQuantities
    if (role.substrate_satisfiable && hasQuantities) return true
    if (role.accepts_subfields && attributes.some((a) =>
      a.subfield_kinds?.some((kind) => role.accepts_subfields?.includes(kind)),
    )) return true
    if (role.target_self) {
      // A self-referential entity-reference exists only when at least one
      // attribute is an entity-reference pointing back at this type.
      return (
        !!currentTypeId &&
        attributes.some(
          (a) => a.kind === 'entity-reference' && a.target_type_id === currentTypeId,
        )
      )
    }
    const matches = attributes.filter(
      (a) => role.accepts.includes(a.kind) && (role.arity === 'repeatable' || !a.is_multiselect),
    ).length
    // Interval roles bind a START and an END — mirror of the backend rule
    // (one date attribute can never fill both).
    return matches >= (role.interval ? 2 : 1)
  }
  if (!spec.roles.every((role) => !role.required || roleIsSatisfiable(role))) return false
  const anyNames = spec.requires_any_roles ?? []
  return anyNames.length === 0 || anyNames.some((name) => {
    const role = spec.roles.find((candidate) => candidate.name === name)
    return Boolean(role && roleIsSatisfiable(role))
  })
}

export function getOfferableViewKinds(
  catalog: ViewKindSpec[],
  attributes: OfferableAttr[],
  hasQuantities: boolean,
  currentTypeId?: string,
  surface?: ConfigSurface,
): ViewKindSpec[] {
  return catalog.filter((spec) => offerable(spec, attributes, hasQuantities, currentTypeId, surface))
}


/** Attribute kinds that can be a GROUP key — the same set the bar/heatmap
 *  group roles accept. */
const CATEGORICAL_KINDS = new Set(['text', 'select', 'enum-set', 'entity-reference'])

/** Expand every `projected_from` option against the subject type's attributes
 *  (2026-09-02): the catalog holds only constants ('all'); the type supplies
 *  its own categorical slugs at edit time. ONE seam, every option, no kind
 *  branch. Attributes absent → static choices only.
 *  INTERNAL — not exported (fix round 1, R1): the unfiltered metadata
 *  projection is not a safe standalone seam (it skips the surface filter).
 *  Call `configurableOptions` instead; that is the one exported entry point
 *  both consumers use. */
function projectOptionChoices(
  schema: OptionField[],
  attributes: Array<{ slug: string; type: string; name?: string }> | undefined,
  quantities: ReadonlyArray<{ slug: string }> = [],
): OptionField[] {
  // the projection vocabulary mirrors monad/view_kinds_registry.py PROJECTIONS
  const projected: Record<string, () => string[]> = {
    categorical_attributes: () => (attributes ?? []).filter((a) => CATEGORICAL_KINDS.has(a.type)).map((a) => a.slug),
    quantities: () => quantities.map((q) => q.slug),
  }
  return schema.map((f) => {
    const from = f.projected_from ? projected[f.projected_from] : undefined
    if (!from) return f
    const slugs = from()
    const merged = [...(f.choices ?? []), ...slugs.filter((s) => !(f.choices ?? []).includes(s))]
    return { ...f, choices: merged }
  })
}

/** SEN-1767: the surface filter. An option that declares no surfaces is offered
 *  on both — the server's own default, repeated here only as WIRE TOLERANCE for
 *  a backend older than SEN-1767 (the same posture `densities` / `marks_supported`
 *  take). The rule itself lives on the served row; this never invents one. */
export function optionsForSurface(
  schema: OptionField[] | undefined,
  surface: ConfigSurface,
): OptionField[] {
  return (schema ?? []).filter((f) => (f.surfaces ?? CONFIG_SURFACES).includes(surface))
}

/** SEN-1756: the structural sibling of `configurableOptions` — the served
 *  structural rows this surface offers. Same wire-tolerance posture: an older
 *  backend serves nothing, the editor renders nothing. */
export function structuralForSurface(
  spec: Pick<ViewKindSpec, 'structural_schema'> | undefined,
  surface: ConfigSurface,
): StructuralField[] {
  return (spec?.structural_schema ?? []).filter((f) =>
    (f.surfaces ?? CONFIG_SURFACES).includes(surface))
}

/** THE seam both option consumers use: the Records-canvas strip's Configure
 *  (App.tsx) and the dashboard WidgetEditor. Surface projection THEN metadata
 *  projection, in that order — a widget-only option must never have its choices
 *  computed for a page that will not render it. An empty result means "this kind
 *  offers nothing here": the caller renders no Configure affordance at all
 *  (truthful affordances — offer only what renders). */
export function configurableOptions(
  spec: Pick<ViewKindSpec, 'config_schema'> | undefined,
  surface: ConfigSurface,
  attributes: Array<{ slug: string; type: string; name?: string }> | undefined,
  /** the binding forms the widget carries (Widget.bind keys): an option that
   *  `requires_bind` a form the widget lacks is not offered (the server would refuse it) */
  bound: readonly string[] = [],
  /** the subject type's quantities, for a `quantities` projection */
  quantities: ReadonlyArray<{ slug: string }> = [],
): OptionField[] {
  return projectOptionChoices(
    optionsForSurface(spec?.config_schema, surface).filter((f) => !f.requires_bind || bound.includes(f.requires_bind)),
    attributes, quantities)
}
